import type { ICountry, IProvince } from '@/entities/country'
import type { ITenantDestination } from '@/entities/tenant-destination'
import type { ITour } from '@/entities/tour'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import { useTenantDestinationsQuery } from '@/entities/tenant-destination'
import { useToursQuery } from '@/entities/tour'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { TourListTable } from '@/features/tour/tour-list-table'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { DEFAULT_FETCH_LIMIT, DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useTenantListScope } from '@/shared/hooks/use-tenant-list-scope'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface OptionItem {
  label: string
  value: string
}

const TOUR_FILTER_VALUE = {
  ALL: '__all__',
} as const

const TOUR_DURATION_BUCKET = {
  ALL: TOUR_FILTER_VALUE.ALL,
  SHORT: 'short',
  MEDIUM: 'medium',
  LONG: 'long',
} as const

const TOUR_ACTIVE_STATUS_FILTER = {
  ALL: TOUR_FILTER_VALUE.ALL,
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

type DurationBucket = (typeof TOUR_DURATION_BUCKET)[keyof typeof TOUR_DURATION_BUCKET]
type StatusFilter = (typeof TOUR_ACTIVE_STATUS_FILTER)[keyof typeof TOUR_ACTIVE_STATUS_FILTER]

/**
 * Renders the tenant tour list with localized data and advanced filters.
 */
export function TourListPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const {
    isSuperAdmin,
    setActiveTenantId,
    tenantOptions,
    currentTenantId,
    currentTenantName,
    selectedLanguageCode,
    setSelectedLanguageCode,
    languageOptions,
    defaultLanguageCode,
    handleLanguageScroll,
  } = useTenantListScope()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedProvinceId, setSelectedProvinceId] = useState('')
  const [selectedTenantDestinationId, setSelectedTenantDestinationId] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(TOUR_ACTIVE_STATUS_FILTER.ACTIVE)
  const [durationBucket, setDurationBucket] = useState<DurationBucket>(TOUR_DURATION_BUCKET.ALL)

  const isActiveFilter =
    statusFilter === TOUR_ACTIVE_STATUS_FILTER.ACTIVE
      ? true
      : statusFilter === TOUR_ACTIVE_STATUS_FILTER.INACTIVE
        ? false
        : undefined
  const { data, isLoading } = useToursQuery(
    currentTenantId,
    {
      current: page,
      pageSize,
      ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
      ...(selectedLanguageCode ? { languageCode: selectedLanguageCode } : {}),
      ...(selectedTenantDestinationId ? { tenantDestinationId: selectedTenantDestinationId } : {}),
    },
    { scope: AdminScopeType.Tenant },
  )

  const { data: countriesResponse } = useCountriesQuery({})
  const { data: provincesResponse } = useProvincesQuery({
    countryId: selectedCountryId,
  })

  const { data: tenantDestinationsResponse } = useTenantDestinationsQuery({
    current: 1,
    pageSize: DEFAULT_FETCH_LIMIT,
    isActive: true,
    ...(selectedLanguageCode ? { languageCode: selectedLanguageCode } : {}),
    ...(selectedCountryId ? { countryId: selectedCountryId } : {}),
    ...(selectedProvinceId ? { provinceId: selectedProvinceId } : {}),
  })

  const countryItems = useMemo<ICountry[]>(() => countriesResponse?.data ?? [], [countriesResponse])

  const provinceItems = useMemo<IProvince[]>(
    () => provincesResponse?.data ?? [],
    [provincesResponse],
  )

  const countryOptions = useMemo(
    () =>
      countryItems.map((country) => ({
        label: country.nativeName ?? country.name ?? country.code,
        value: country.id,
      })),
    [countryItems],
  )

  const provinceOptions = useMemo(
    () =>
      provinceItems.map((province) => ({
        label: province.nativeName ?? province.name ?? province.code,
        value: province.id,
      })),
    [provinceItems],
  )

  const effectiveLanguageCode = selectedLanguageCode || defaultLanguageCode
  const tenantDestinationOptions = useMemo(
    () =>
      (tenantDestinationsResponse?.data?.data ?? []).map((item: ITenantDestination) => ({
        label: item.localizedData?.[effectiveLanguageCode]?.name ?? 'No content available',
        value: item.id,
      })),
    [tenantDestinationsResponse?.data?.data, effectiveLanguageCode],
  )

  const hasLocationFilter = Boolean(
    selectedCountryId || selectedProvinceId || selectedTenantDestinationId,
  )
  const activeFilterCount = [
    selectedLanguageCode && selectedLanguageCode !== defaultLanguageCode
      ? selectedLanguageCode
      : '',
    selectedCountryId,
    selectedProvinceId,
    selectedTenantDestinationId,
    statusFilter !== TOUR_ACTIVE_STATUS_FILTER.ALL ? statusFilter : '',
    durationBucket !== TOUR_DURATION_BUCKET.ALL ? durationBucket : '',
  ].filter(Boolean).length

  const tours = useMemo(() => {
    const items: ITour[] = data?.data ?? []
    const keyword = searchTerm.trim().toLowerCase()
    return items.filter((tour) => {
      if (keyword) {
        const matchesKeyword = tour.localizedData.some(
          (tr) =>
            tr.name.toLowerCase().includes(keyword) ||
            (tr.description ?? '').toLowerCase().includes(keyword),
        )
        if (!matchesKeyword) {
          return false
        }
      }

      if (durationBucket !== TOUR_DURATION_BUCKET.ALL) {
        if (durationBucket === TOUR_DURATION_BUCKET.SHORT && tour.duration > 60) {
          return false
        }
        if (
          durationBucket === TOUR_DURATION_BUCKET.MEDIUM &&
          (tour.duration <= 60 || tour.duration > 120)
        ) {
          return false
        }
        if (durationBucket === TOUR_DURATION_BUCKET.LONG && tour.duration <= 120) {
          return false
        }
      }

      if (!hasLocationFilter) {
        return true
      }

      return true
    })
  }, [data?.data, durationBucket, hasLocationFilter, searchTerm, statusFilter])

  const hasClientFilters = Boolean(searchTerm.trim() || durationBucket !== TOUR_DURATION_BUCKET.ALL)

  const total = hasClientFilters ? tours.length : currentTenantId ? (data?.total ?? 0) : 0

  /**
   * Opens the selected tour edit page for the active tenant.
   */
  function handleEdit(tour: ITour) {
    if (!currentTenantId) return
    void navigate({
      to: scopedAdminPath(
        generatePath('/tenant/:tenantId/tours/:tourId/edit', {
          tenantId: currentTenantId,
          tourId: tour.id,
        }),
      ) as never,
    })
  }

  /**
   * Restores tour filters to defaults for the active tenant and language.
   */
  function resetAdvancedFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setSelectedCountryId('')
    setSelectedProvinceId('')
    setSelectedTenantDestinationId('')
    setStatusFilter(TOUR_ACTIVE_STATUS_FILTER.ACTIVE)
    setDurationBucket(TOUR_DURATION_BUCKET.ALL)
    setPage(1)
  }

  /**
   * Switches the active tenant and resets the filters/queries scoped to the previous tenant.
   */
  function handleTenantChange(tenantId: string) {
    setActiveTenantId(tenantId)
    setPage(1)
    setSelectedCountryId('')
    setSelectedProvinceId('')
    setSelectedTenantDestinationId('')
    setSelectedLanguageCode('')
    void queryClient.invalidateQueries({ queryKey: ['tours'] })
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('tour.title')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(currentTenantName
            ? [
                {
                  label: currentTenantName,
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('sidebar.tours') },
        ]}
        actions={
          <Button
            disabled={!currentTenantId}
            onClick={() => {
              void navigate({
                to: scopedAdminPath(
                  generatePath('/tenant/:tenantId/tours/create', { tenantId: currentTenantId }),
                ) as never,
              })
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t('tour.create')}
          </Button>
        }
      />

      <DataTableToolbar>
        {isSuperAdmin && (
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select value={currentTenantId || ''} onValueChange={handleTenantChange}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt: OptionItem) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        )}

        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[240px]"
        >
          <TenantSearchBar
            className="w-64"
            onSearch={(term) => {
              setSearchTerm(term)
              setPage(1)
            }}
          />
        </DataTableFilterField>

        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={resetAdvancedFilters}
        >
          <DataTableFilterField label={t('common.language')}>
            <Select
              value={selectedLanguageCode || defaultLanguageCode}
              onValueChange={(value) => {
                setSelectedLanguageCode(value)
                setPage(1)
              }}
              disabled={languageOptions.length < 1}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.language')} />
              </SelectTrigger>
              <SelectContent onViewportScroll={handleLanguageScroll}>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.country')}>
            <Select
              {...(selectedCountryId
                ? { value: selectedCountryId }
                : { value: TOUR_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setSelectedCountryId(value === TOUR_FILTER_VALUE.ALL ? '' : value)
                setSelectedProvinceId('')
                setSelectedTenantDestinationId('')
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.country')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TOUR_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.province')}>
            <Select
              {...(selectedProvinceId
                ? { value: selectedProvinceId }
                : { value: TOUR_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setSelectedProvinceId(value === TOUR_FILTER_VALUE.ALL ? '' : value)
                setSelectedTenantDestinationId('')
                setPage(1)
              }}
              disabled={!selectedCountryId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.province')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TOUR_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {provinceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField
            label={t('tenantDestination.title', { defaultValue: 'Tenant destination' })}
          >
            <Select
              {...(selectedTenantDestinationId
                ? { value: selectedTenantDestinationId }
                : { value: TOUR_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setSelectedTenantDestinationId(value === TOUR_FILTER_VALUE.ALL ? '' : value)
                setPage(1)
              }}
              disabled={tenantDestinationOptions.length < 1}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t('tenantDestination.title', { defaultValue: 'Tenant destination' })}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TOUR_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {tenantDestinationOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('common.status')}>
            <Select
              value={statusFilter}
              onValueChange={(value: StatusFilter) => {
                setStatusFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TOUR_ACTIVE_STATUS_FILTER.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={TOUR_ACTIVE_STATUS_FILTER.ACTIVE}>
                  {t('common.active', { defaultValue: 'Hoạt động' })}
                </SelectItem>
                <SelectItem value={TOUR_ACTIVE_STATUS_FILTER.INACTIVE}>
                  {t('common.inactive', { defaultValue: 'Ngưng hoạt động' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('tour.duration')}>
            <Select
              value={durationBucket}
              onValueChange={(value: DurationBucket) => {
                setDurationBucket(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('tour.duration')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TOUR_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={TOUR_DURATION_BUCKET.SHORT}>
                  {t('tour.durationBuckets.short')}
                </SelectItem>
                <SelectItem value={TOUR_DURATION_BUCKET.MEDIUM}>
                  {t('tour.durationBuckets.medium')}
                </SelectItem>
                <SelectItem value={TOUR_DURATION_BUCKET.LONG}>
                  {t('tour.durationBuckets.long')}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </DataTableToolbar>
      {hasLocationFilter && (
        <p className="mb-4 text-xs text-zinc-500">
          {t('tour.locationFilterHint', {
            defaultValue:
              'Bộ lọc địa lý đang áp dụng theo POI của lộ trình trong trang dữ liệu hiện tại.',
          })}
        </p>
      )}

      <TourListTable
        tours={tours}
        loading={currentTenantId ? isLoading : false}
        pagination={{ current: page, total, pageSize }}
        languageCode={selectedLanguageCode || defaultLanguageCode}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage)
          setPageSize(nextPageSize)
        }}
        onEdit={handleEdit}
      />
    </PageContainer>
  )
}
